import { Button, Stack } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useProducts } from "../admin/products/useProducts";
import type { CalculationProductOption, SavedCalculationProductOption } from "../calculations/calculationTypes";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import ParticipantCountDiscrepancyDialog from "../components/participantCountDiscrepancyDialog/ParticipantCountDiscrepancyDialog";
import { findSaveDiscrepancies, type SaveDiscrepancies } from "../sharedComponents/participantCountValidation";
import BackToOrderListButton from "./BackToOrderListButton";
import OrderCancelBookingsButton from "./bookings/OrderCancelBookingsButton";
import CreateCalculationButton from "./CreateCalculationButton";
import DownloadOrderPdfButton from "./DownloadOrderPdfButton";
import OrderEmailButton from "./emails/OrderEmailButton";
import GoToParticipantListButton from "./GoToParticipantListButton";
import OrderInvoiceButton from "./invoices/OrderInvoiceButton";
import GenerateOrderConfirmationButton from "./orderConfirmation/GenerateOrderConfirmationButton";
import type { SavedOrder } from "./orderTypes";

interface Props {
  readonly order: SavedOrder;
  readonly onDuplicate: () => void;
  readonly onGoToParticipantList: () => void;
  readonly onCreateCalculation: () => void;
  readonly onSubmit: () => Promise<void>;
  readonly onDelete: () => Promise<void>;
  readonly onRequestQuote: () => Promise<void>;
}

export const EditOrderAdminActions: FC<Props> = ({
  order,
  onDuplicate,
  onGoToParticipantList,
  onCreateCalculation,
  onSubmit,
  onDelete,
  onRequestQuote,
}) => {
  const [confirmationDialog, setConfirmationDialog] = useState<{
    title: string;
    content: string;
    onConfirm: () => Promise<void>;
  }>();
  const [discrepancyModal, setDiscrepancyModal] = useState<SaveDiscrepancies | null>(null);
  const { products } = useProducts();
  const isECommerceOrder = order.customerId === "e-commerce-customer";
  const hasProductsForQuote = order.products.some(({ id, options }) => {
    const providerHasEmailRecipient = products.find((p) => p.id === id)?.provider.hasEmailRecipients;
    return (
      providerHasEmailRecipient &&
      options.some((o: SavedCalculationProductOption | CalculationProductOption) => o.price === "ON_DEMAND")
    );
  });

  const handleRequestQuote = () => {
    setConfirmationDialog({
      title: "Zamów wycenę",
      content: "Czy chcesz wysłać prośbę do dostawcy o przygotowanie wyceny dla opcji 'na zapytanie'?",
      onConfirm: async () => {
        await onRequestQuote();
        setConfirmationDialog(undefined);
      },
    });
  };

  const handleDelete = () => {
    setConfirmationDialog({
      title: "Usuń zamówienie",
      content: "Czy chcesz usunąć zamówienie? Tej operacji nie będzie można już cofnąć.",
      onConfirm: async () => {
        await onDelete();
        setConfirmationDialog(undefined);
      },
    });
  };

  const handleSubmit = (): Promise<void> => {
    const totalParticipants = order.participantsNumber.numberOfPaid + order.participantsNumber.numberOfStaff;
    const discrepancies = findSaveDiscrepancies(totalParticipants, order.products, products);
    if (discrepancies) {
      setDiscrepancyModal(discrepancies);
      return Promise.resolve();
    }
    return onSubmit();
  };

  const handleDiscrepancyConfirm = async () => {
    setDiscrepancyModal(null);
    await onSubmit();
  };

  return (
    <Stack direction="row" spacing={2}>
      <BackToOrderListButton />
      <Button variant="outlined" color="error" onClick={handleDelete}>
        Usuń
      </Button>
      <OrderCancelBookingsButton orderId={order.id} />
      <Button variant="outlined" color="secondary" onClick={onDuplicate}>
        Duplikuj
      </Button>
      <GoToParticipantListButton order={order} onClick={onGoToParticipantList} />
      <CreateCalculationButton onClick={onCreateCalculation} />
      <OrderEmailButton order={order} />
      {!isECommerceOrder && <OrderInvoiceButton order={order} />}
      <DownloadOrderPdfButton orderId={order.id} orderType={order.type} filename={`${order.name}.pdf`} />
      {!isECommerceOrder && <GenerateOrderConfirmationButton order={order} />}
      {hasProductsForQuote && (
        <Button variant="outlined" onClick={handleRequestQuote}>
          Zamów wycenę
        </Button>
      )}
      <AsyncLoadingButton onClick={handleSubmit}>Zapisz</AsyncLoadingButton>
      <ConfirmationDialog
        open={!!confirmationDialog}
        title={confirmationDialog?.title ?? ""}
        content={confirmationDialog?.content ?? ""}
        onCancel={() => setConfirmationDialog(undefined)}
        onConfirm={confirmationDialog?.onConfirm ?? (() => Promise.resolve())}
      />
      <ParticipantCountDiscrepancyDialog
        discrepancies={discrepancyModal}
        onConfirm={handleDiscrepancyConfirm}
        onCancel={() => setDiscrepancyModal(null)}
      />
    </Stack>
  );
};

export default EditOrderAdminActions;
